import { useCallback, useEffect, useRef, useState } from 'react'

// Generic keyset/cursor-paginated list. Reusable by any feature that lists rows
// from a `nextCursor`-style endpoint (searchrooms now, code tab later).
//
// `fetchPage` MUST be stable (wrap it in useCallback) and shaped:
//   fetchPage({ cursor, params, limit }) -> Promise<{ items, nextCursor, hasMore }>
//
// Whenever `params` change (compared by JSON) the list resets and reloads from
// the first page. An in-flight request is invalidated by a newer one via a
// request token, so rapid param changes never interleave results.
export default function useEntityList({ fetchPage, params = null, pageSize = 30 }) {
  const [items, setItems] = useState([])
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState('')

  const cursorRef = useRef(null)
  const busyRef = useRef(false)
  const tokenRef = useRef(0)
  const paramsRef = useRef(params)
  paramsRef.current = params

  const key = params ? JSON.stringify(params) : ''

  const load = useCallback(
    async (reset) => {
      const token = ++tokenRef.current
      busyRef.current = true
      if (reset) {
        cursorRef.current = null
        setLoading(true)
      } else {
        setLoadingMore(true)
      }
      try {
        const res = await fetchPage({
          cursor: cursorRef.current,
          params: paramsRef.current,
          limit: pageSize,
        })
        if (token !== tokenRef.current) return
        cursorRef.current = res.nextCursor || null
        setHasMore(Boolean(res.hasMore))
        setItems((prev) => (reset ? res.items : [...prev, ...res.items]))
        setError('')
      } catch (err) {
        if (token === tokenRef.current) setError(err.message || 'Could not load.')
      } finally {
        if (token === tokenRef.current) {
          if (reset) setLoading(false)
          else setLoadingMore(false)
          busyRef.current = false
        }
      }
    },
    [fetchPage, pageSize]
  )

  // Reset + reload when the fetcher or params change.
  useEffect(() => {
    load(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, key])

  const loadMore = useCallback(() => {
    if (busyRef.current || !hasMore) return
    load(false)
  }, [load, hasMore])

  const reload = useCallback(() => load(true), [load])

  return { items, loading, loadingMore, hasMore, error, loadMore, reload }
}
